import {ErrorWithContext, MultipleLineError} from "@dvrt/graphql-widgets";
import {Button, ButtonProps, useTheme} from "@mui/material";
import * as React from "react";

type Props = {
    acceptFileType?: string;
    errors?: ErrorWithContext[];
} & (
    | {
          acceptMultiple?: false;
          onChange: (file: File | null) => void;
      }
    | {
          acceptMultiple: true;
          onChange: (files: File[] | null) => void;
      }
) &
    Pick<ButtonProps, "children" | "id" | "style" | "color" | "disabled" | "variant" | "size" | "fullWidth">;

/**
 * A button that opens a file selector dialog.
 *
 * There is no component for this in Material UI, so that is why this was built.
 *
 * @param acceptMultiple Whether to allow multiple files to be selected.
 * @param acceptFileType The file type to accept. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#Unique_file_type_specifiers
 * @param onChange The callback to call when the user selects a file. The FileList is passed as argument.
 * @param rest Some props are passed to the underlying button that are used throughout the app.
 *
 * @example
 * const [file, setFile] = React.useState<File | null>(null);
 *
 * <FileSelector
 *     acceptFileType=".xlsx"
 *     onChange={setFile)
 *     variant="contained"
 *     color="primary"
 * >
 *    Select file
 * </FileSelector>
 *
 * @example
 * const [files, setFiles] = React.useState<File[] | null>(null);
 * <FileSelector
 *   acceptMultiple
 *   acceptFileType=".xlsx"
 *   onChange={setFiles}
 *   variant="contained"
 *   color="primary"
 * >
 *    Select files
 * </FileSelector>
 */
export default ({acceptFileType, onChange, acceptMultiple, errors, children, ...buttonProps}: Props) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const theme = useTheme();
    const errorColor = theme.palette.error.main;

    const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        if (ev.target.files) {
            if (acceptMultiple) {
                (onChange as (file: File[] | null) => void)?.(Array.from(ev.target.files));
            } else {
                (onChange as (file: File | null) => void)?.(ev.target.files[0] || null);
            }
        }
        inputRef.current.value = null;
    };

    const handleClick = () => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    };

    return (
        <div>
            <input
                ref={inputRef}
                type="file"
                multiple={acceptMultiple}
                accept={acceptFileType}
                hidden
                onChange={handleChange}
            />
            <Button {...buttonProps} onClick={handleClick}>
                {children}
            </Button>
            <div style={{color: errorColor}}>
                <MultipleLineError errors={errors} />
            </div>
        </div>
    );
};
